import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Stack,
  Tabs,
  Tab,
  TextField,
  InputAdornment,
  Collapse,
  Menu,
  MenuItem,
  Button,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  RestoreFromTrash as RestoreIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
  Send as SendIcon,
  Inventory as ReceivedIcon,
  Cancel as CancelIcon,
  RestartAlt as ReopenIcon,
  MoreVert as MoreVertIcon,
  Print as PrintIcon,
} from '@mui/icons-material';
import { BonCommande, BonCommandeStatus, BonCommandeFilters } from '../../../types/bonCommande';
import {
  bonsCommandeAPI,
  auditLogsAPI,
} from '../../../services/api';
// generateBonCommandePDF is dynamically imported in handlePrint so the
// @react-pdf/renderer chunk only loads when the user clicks Print.
import AlertDialog from '../../../components/AlertDialog';
import useAlert from '../../../hooks/useAlert';
import { getModuleTableTheme } from '../../../theme/tableTheme';
import { extractErrorMessage } from '../../../utils/errorHandler';
import usePdfPreview from '../../../hooks/usePdfPreview';
import PermissionButton from '../../../components/PermissionButton';
import CustomTablePagination from '../../../components/CustomTablePagination';
import ColumnFilter from '../../../components/ColumnFilter';
import { useColumnFilters } from '../../../hooks/useColumnFilters';
import BonCommandeFormDialog from '../BonCommandeFormDialog';
import { useAuth } from '../../../contexts/AuthContext';
import {
  useBonsCommandeList,
  useDeletedBonsCommande,
  useInvalidateBonsCommande,
} from '../../../hooks/queries/useBonsCommande';
import { useSuppliersDropdown, useUsersDropdown } from '../../../hooks/queries/useDropdowns';

const STOCK_COLOR = '#2196F3';
const MANAGER_ROLES = ['admin', 'Manager', 'Developer'];
const BC_TVA_RATE = 20; // Fixed TVA for all Bons de Commande

// Round to 2 decimals (kills float arithmetic noise)
const round2 = (n: number): number => Math.round(n * 100) / 100;

// Compute HT/TVA/TTC by rounding HT first, then multiplying by (1 + tva/100)
// directly — matches the devis pattern (`Math.round(montant_total * 1.2 * 100) / 100`)
// and guarantees HT + TVA = TTC always.
const computeBcTotals = (rawHt: number) => {
  const ht = round2(rawHt);
  const ttc = round2(ht * (1 + BC_TVA_RATE / 100));
  const tva = round2(ttc - ht);
  return { ht, tva, ttc };
};

const BC_FILTER_SCHEMA = {
  date:         { type: 'dateRange',         paramFrom: 'start_date', paramTo: 'end_date' },
  reference:    { type: 'text',              param: 'reference' },
  user_ids:     { type: 'multiAutocomplete', param: 'user_ids' },
  supplier_ids: { type: 'multiAutocomplete', param: 'supplier_ids' },
  statuses:     { type: 'multiSelect',       param: 'statuses' },
} as const;

const STATUS_OPTIONS = [
  { value: BonCommandeStatus.BROUILLON, label: 'Brouillon' },
  { value: BonCommandeStatus.ENVOYEE, label: 'Envoyée' },
  { value: BonCommandeStatus.RECUE, label: 'Reçue' },
  { value: BonCommandeStatus.ANNULEE, label: 'Annulée' },
];

const getStatusChip = (status: string) => {
  switch (status) {
    case BonCommandeStatus.BROUILLON:
      return <Chip label="Brouillon" size="small" sx={{ bgcolor: 'grey.500', color: 'white' }} />;
    case BonCommandeStatus.ENVOYEE:
      return <Chip label="Envoyée" color="info" size="small" />;
    case BonCommandeStatus.RECUE:
      return <Chip label="Reçue" color="success" size="small" />;
    case BonCommandeStatus.ANNULEE:
      return <Chip label="Annulée" color="error" size="small" />;
    default:
      return <Chip label={status} size="small" />;
  }
};

const formatDate = (iso?: string | null) => {
  if (!iso) return '-';
  return new Date(iso).toLocaleDateString('fr-FR');
};

const formatMoney = (n?: number | null) => {
  if (n == null) return '0,00';
  return Number(n).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const BonsCommandeManagement: React.FC = () => {
  const { user } = useAuth();
  const isManager = useMemo(
    () => MANAGER_ROLES.includes(user?.role || '') || MANAGER_ROLES.includes(user?.user_role?.name || ''),
    [user],
  );

  // UI state
  const [tabValue, setTabValue] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const { filters, update, reset, hasActive, toQueryParams } = useColumnFilters(
    BC_FILTER_SCHEMA,
    { onChange: () => setPage(0) }
  );
  const { data: users = [] } = useUsersDropdown(isManager); // cached; only for managers
  const { data: suppliers = [] } = useSuppliersDropdown();
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());

  // Form dialog
  const [formOpen, setFormOpen] = useState(false);
  const [formMinimized, setFormMinimized] = useState(false);
  const [editingId, setEditingId] = useState<number | undefined>(undefined);

  // Status menu
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [menuTargetBC, setMenuTargetBC] = useState<BonCommande | null>(null);

  const { alertState, showError, showSuccess, showConfirm, hideAlert } = useAlert();
  const { openPreview, pdfPreviewElement } = usePdfPreview();

  // Debounce search
  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setPage(0);
    }, 300);
    return () => clearTimeout(t);
  }, [searchTerm]);

  // Server-paginated active list + deleted list via TanStack Query.
  // Query key is rooted at the 'bons-commande' slug so realtime
  // invalidation (another user's change) auto-refreshes this view.
  const listParams = useMemo(() => {
    const filterBag = toQueryParams() as BonCommandeFilters;
    return {
      skip: page * rowsPerPage,
      limit: rowsPerPage,
      search: debouncedSearch || undefined,
      filters: Object.keys(filterBag).length > 0 ? filterBag : undefined,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, debouncedSearch, filters]);

  const activeQuery = useBonsCommandeList(listParams, tabValue === 0);
  const deletedQuery = useDeletedBonsCommande(tabValue === 1);
  const invalidate = useInvalidateBonsCommande();

  const items = activeQuery.data?.items ?? [];
  const totalCount = activeQuery.data?.total ?? 0;
  const deletedItems = deletedQuery.data ?? [];
  const loading = tabValue === 0 ? activeQuery.isInitialLoading : deletedQuery.isInitialLoading;

  useEffect(() => {
    if (activeQuery.error) showError(extractErrorMessage(activeQuery.error));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeQuery.error]);

  useEffect(() => {
    if (deletedQuery.error) showError(extractErrorMessage(deletedQuery.error));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deletedQuery.error]);

  // Fetch users for the operator filter (managers only)

  const handleChangePage = (_: unknown, newPage: number) => setPage(newPage);
  const handleChangeRowsPerPage = (e: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  const toggleExpand = (id: number) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // Form dialog handlers
  const handleOpenAdd = () => {
    setEditingId(undefined);
    setFormMinimized(false);
    setFormOpen(true);
  };

  const handleOpenEdit = (id: number) => {
    setEditingId(id);
    setFormMinimized(false);
    setFormOpen(true);
  };

  const handleFormSuccess = () => {
    invalidate();
  };

  // Delete / Restore
  const handleDelete = (bc: BonCommande) => {
    showConfirm(
      `Êtes-vous sûr de vouloir supprimer le bon de commande "${bc.reference}" ? La demande d'achat liée reviendra au statut "Validé".`,
      async () => {
        try {
          await bonsCommandeAPI.delete(bc.id);
          showSuccess('Bon de commande supprimé avec succès');
          invalidate();
        } catch (err: any) {
          showError(extractErrorMessage(err));
        }
      },
      { title: 'Confirmer la suppression', confirmText: 'Supprimer' },
    );
  };

  const handleRestore = (bc: BonCommande) => {
    showConfirm(
      `Restaurer le bon de commande "${bc.reference}" ?`,
      async () => {
        try {
          await bonsCommandeAPI.restore(bc.id);
          showSuccess('Bon de commande restauré avec succès');
          invalidate();
        } catch (err: any) {
          showError(extractErrorMessage(err));
        }
      },
      { title: 'Confirmer la restauration', confirmText: 'Restaurer' },
    );
  };

  // Status menu
  const openStatusMenu = (event: React.MouseEvent<HTMLElement>, bc: BonCommande) => {
    setMenuAnchor(event.currentTarget);
    setMenuTargetBC(bc);
  };

  const closeStatusMenu = () => {
    setMenuAnchor(null);
    setMenuTargetBC(null);
  };

  const triggerStatusChange = async (newStatus: string, label: string) => {
    if (!menuTargetBC) return;
    closeStatusMenu();
    try {
      await bonsCommandeAPI.updateStatus(menuTargetBC.id, { status: newStatus });
      showSuccess(`Statut mis à jour: ${label}`);
      invalidate();
    } catch (err: any) {
      showError(extractErrorMessage(err));
    }
  };

  const handlePrint = (bc: BonCommande) => {
    openPreview({
      getBlob: async () => {
        // Always reload to get fresh items + enriched supplier/condition/mode names
        const fresh = await bonsCommandeAPI.getById(bc.id);
        const { getBonCommandePDFBlob } = await import('../../../utils/bonCommandePdfReactPdf');
        return getBonCommandePDFBlob(fresh, { includeOperatorName: false });
      },
      fileName: `BC_${bc.reference}.pdf`,
    });
    try {
      auditLogsAPI.logPdfDownload('BonCommande', bc.id, `Aperçu PDF - ${bc.reference}`);
    } catch {
      /* audit failure shouldn't break print */
    }
  };

  const handleMarkReceived = (bc: BonCommande) => {
    showConfirm(
      `Marquer le bon de commande "${bc.reference}" comme reçu ? La demande d'achat liée passera en "Réceptionné".`,
      async () => {
        try {
          await bonsCommandeAPI.markReceived(bc.id);
          showSuccess('Bon de commande marqué comme reçu');
          invalidate();
        } catch (err: any) {
          showError(extractErrorMessage(err));
        }
      },
      { title: 'Confirmer la réception', confirmText: 'Marquer reçu' },
    );
  };

  const renderActiveTable = () => (
    <>
      <TableContainer sx={getModuleTableTheme('stock')}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 50 }} />
              <TableCell sx={{ whiteSpace: 'nowrap' }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  Date
                  <ColumnFilter
                    type="dateRange"
                    value={filters.date}
                    onChange={(val) => update('date', val)}
                    label="Date"
                    color={STOCK_COLOR}
                  />
                </Box>
              </TableCell>
              <TableCell sx={{ whiteSpace: 'nowrap' }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  Opérateur
                  {isManager && (
                    <ColumnFilter
                      type="multiAutocomplete"
                      value={filters.user_ids}
                      onChange={(val) => update('user_ids', val)}
                      options={users.map((u) => ({ value: u.id, label: u.full_name || u.email }))}
                      label="Opérateur"
                      color={STOCK_COLOR}
                    />
                  )}
                </Box>
              </TableCell>
              <TableCell sx={{ whiteSpace: 'nowrap' }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  N° BC
                  <ColumnFilter
                    type="text"
                    value={filters.reference}
                    onChange={(val) => update('reference', val)}
                    label="Référence"
                    placeholder="Ex: BC26-001"
                    color={STOCK_COLOR}
                  />
                </Box>
              </TableCell>
              <TableCell sx={{ whiteSpace: 'nowrap' }}>DA liée</TableCell>
              <TableCell sx={{ whiteSpace: 'nowrap' }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  Fournisseur
                  <ColumnFilter
                    type="multiAutocomplete"
                    value={filters.supplier_ids}
                    onChange={(val) => update('supplier_ids', val)}
                    options={suppliers.map((s) => ({ value: s.id, label: `${s.code} - ${s.name}` }))}
                    label="Fournisseur"
                    color={STOCK_COLOR}
                  />
                </Box>
              </TableCell>
              <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>Total H.T / T.T.C</TableCell>
              <TableCell align="center">
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  Statut
                  <ColumnFilter
                    type="multiSelect"
                    value={filters.statuses}
                    onChange={(val) => update('statuses', val)}
                    options={STATUS_OPTIONS}
                    label="Statut"
                    color={STOCK_COLOR}
                  />
                </Box>
              </TableCell>
              <TableCell align="center" sx={{ width: 200 }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                  <CircularProgress size={28} />
                </TableCell>
              </TableRow>
            ) : items.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">Aucun bon de commande</Typography>
                </TableCell>
              </TableRow>
            ) : (
              items.map((bc) => {
                const isExpanded = expandedRows.has(bc.id);
                const canEdit =
                  isManager ||
                  (bc.status === BonCommandeStatus.BROUILLON || bc.status === BonCommandeStatus.ENVOYEE);
                const canMarkReceived =
                  isManager &&
                  (bc.status === BonCommandeStatus.BROUILLON || bc.status === BonCommandeStatus.ENVOYEE);
                return (
                  <React.Fragment key={bc.id}>
                    <TableRow hover>
                      <TableCell>
                        <IconButton size="small" onClick={() => toggleExpand(bc.id)}>
                          {isExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                        </IconButton>
                      </TableCell>
                      <TableCell>{formatDate(bc.date)}</TableCell>
                      <TableCell>{bc.user_name || '-'}</TableCell>
                      <TableCell>
                        <Typography fontWeight={600} sx={{ color: STOCK_COLOR }}>
                          {bc.reference}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {bc.da_reference ? (
                          <Chip size="small" label={bc.da_reference} variant="outlined" />
                        ) : (
                          '-'
                        )}
                      </TableCell>
                      <TableCell>
                        {bc.supplier_name ? (
                          <Box>
                            <Typography variant="body2" fontWeight={600}>{bc.supplier_code}</Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ display: 'block', maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                            >
                              {bc.supplier_name}
                            </Typography>
                          </Box>
                        ) : (
                          '-'
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {(() => {
                          const t = computeBcTotals(Number(bc.montant_total) || 0);
                          return (
                            <>
                              <Typography variant="body2" fontWeight={600}>
                                {formatMoney(t.ht)} DH
                              </Typography>
                              <Typography variant="caption" sx={{ color: STOCK_COLOR, fontWeight: 700 }}>
                                {formatMoney(t.ttc)} DH TTC
                              </Typography>
                            </>
                          );
                        })()}
                      </TableCell>
                      <TableCell align="center">{getStatusChip(bc.status)}</TableCell>
                      <TableCell align="center">
                        <Stack direction="row" spacing={0.5} justifyContent="center">
                          <Tooltip title="Imprimer / Télécharger PDF">
                            <IconButton
                              size="small"
                              onClick={() => handlePrint(bc)}
                              sx={{ color: STOCK_COLOR }}
                            >
                              <PrintIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          {canMarkReceived && (
                            <Tooltip title="Marquer comme reçue">
                              <IconButton
                                size="small"
                                onClick={() => handleMarkReceived(bc)}
                                sx={{ color: 'success.main' }}
                              >
                                <ReceivedIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          {isManager && (
                            <Tooltip title="Changer le statut">
                              <IconButton
                                size="small"
                                onClick={(e) => openStatusMenu(e, bc)}
                                sx={{ color: STOCK_COLOR }}
                              >
                                <MoreVertIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          <PermissionButton
                            moduleName="Stock"
                            operation="edit"
                            miniModuleName="Bons Commande"
                            component="IconButton"
                            disabled={!canEdit}
                            tooltipTitle={
                              !canEdit
                                ? 'Modification bloquée pour ce statut'
                                : undefined
                            }
                            onClick={() => handleOpenEdit(bc.id)}
                          >
                            <EditIcon fontSize="small" />
                          </PermissionButton>
                          <PermissionButton
                            moduleName="Stock"
                            operation="delete"
                            miniModuleName="Bons Commande"
                            component="IconButton"
                            color="error"
                            onClick={() => handleDelete(bc)}
                          >
                            <DeleteIcon fontSize="small" />
                          </PermissionButton>
                        </Stack>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={9}>
                        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                          <Box sx={{ m: 2, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                            <Typography variant="subtitle2" gutterBottom sx={{ color: STOCK_COLOR, fontWeight: 600 }}>
                              Articles commandés
                            </Typography>
                            <Table size="small" sx={{ mb: 2 }}>
                              <TableHead>
                                <TableRow>
                                  <TableCell>Désignation</TableCell>
                                  <TableCell align="right">Quantité</TableCell>
                                  <TableCell align="right">Prix unitaire</TableCell>
                                  <TableCell align="right">Total</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {bc.items.map((it) => (
                                  <TableRow key={it.id}>
                                    <TableCell>
                                      {it.article_reference && (
                                        <Typography variant="caption" color="text.secondary" display="block">
                                          {it.article_reference}
                                        </Typography>
                                      )}
                                      <Typography variant="body2">{it.designation}</Typography>
                                    </TableCell>
                                    <TableCell align="right">
                                      {Number(it.quantite).toLocaleString('fr-FR')}
                                      {it.unit ? ` ${it.unit}` : ''}
                                    </TableCell>
                                    <TableCell align="right">{formatMoney(it.prix_unitaire)} DH</TableCell>
                                    <TableCell align="right">{formatMoney(it.total)} DH</TableCell>
                                  </TableRow>
                                ))}
                                {(() => {
                                  const t = computeBcTotals(Number(bc.montant_total) || 0);
                                  return (
                                    <>
                                      <TableRow>
                                        <TableCell colSpan={3} align="right" sx={{ fontWeight: 600 }}>
                                          Total H.T
                                        </TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 600 }}>
                                          {formatMoney(t.ht)} DH
                                        </TableCell>
                                      </TableRow>
                                      <TableRow>
                                        <TableCell colSpan={3} align="right" sx={{ fontWeight: 600 }}>
                                          T.V.A {BC_TVA_RATE}%
                                        </TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 600 }}>
                                          {formatMoney(t.tva)} DH
                                        </TableCell>
                                      </TableRow>
                                      <TableRow>
                                        <TableCell colSpan={3} align="right" sx={{ fontWeight: 700, color: STOCK_COLOR }}>
                                          Total T.T.C
                                        </TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 700, color: STOCK_COLOR }}>
                                          {formatMoney(t.ttc)} DH
                                        </TableCell>
                                      </TableRow>
                                    </>
                                  );
                                })()}
                              </TableBody>
                            </Table>

                            <Stack direction="row" spacing={3} sx={{ mt: 1, flexWrap: 'wrap' }}>
                              {bc.condition_nom && (
                                <Box>
                                  <Typography variant="caption" color="text.secondary" display="block">Condition de paiement</Typography>
                                  <Typography variant="body2">{bc.condition_nom}</Typography>
                                </Box>
                              )}
                              {bc.mode_nom && (
                                <Box>
                                  <Typography variant="caption" color="text.secondary" display="block">Mode de paiement</Typography>
                                  <Typography variant="body2">{bc.mode_nom}</Typography>
                                </Box>
                              )}
                            </Stack>

                            {bc.remarque && (
                              <Box sx={{ mt: 2, p: 1.5, bgcolor: 'background.paper', borderRadius: 1 }}>
                                <Typography variant="caption" fontWeight={600}>Remarque:</Typography>
                                <Typography variant="body2">{bc.remarque}</Typography>
                              </Box>
                            )}
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <CustomTablePagination
        count={totalCount}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </>
  );

  const renderDeletedTable = () => (
    <TableContainer sx={getModuleTableTheme('stock')}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell sx={{ whiteSpace: 'nowrap' }}>Date</TableCell>
            <TableCell sx={{ whiteSpace: 'nowrap' }}>Opérateur</TableCell>
            <TableCell sx={{ whiteSpace: 'nowrap' }}>N° BC</TableCell>
            <TableCell sx={{ whiteSpace: 'nowrap' }}>DA liée</TableCell>
            <TableCell sx={{ whiteSpace: 'nowrap' }}>Fournisseur</TableCell>
            <TableCell align="right">Total</TableCell>
            <TableCell align="center">Statut</TableCell>
            <TableCell align="center">Supprimé le</TableCell>
            <TableCell align="center">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {loading ? (
            <TableRow>
              <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                <CircularProgress size={28} />
              </TableCell>
            </TableRow>
          ) : deletedItems.length === 0 ? (
            <TableRow>
              <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                <Typography color="text.secondary">Aucun BC supprimé</Typography>
              </TableCell>
            </TableRow>
          ) : (
            deletedItems.map((bc) => (
              <TableRow key={bc.id} hover>
                <TableCell>{formatDate(bc.date)}</TableCell>
                <TableCell>{bc.user_name || '-'}</TableCell>
                <TableCell>
                  <Typography fontWeight={600} sx={{ color: STOCK_COLOR }}>
                    {bc.reference}
                  </Typography>
                </TableCell>
                <TableCell>{bc.da_reference || '-'}</TableCell>
                <TableCell>{bc.supplier_name || '-'}</TableCell>
                <TableCell align="right">{formatMoney(bc.montant_total)} DH</TableCell>
                <TableCell align="center">{getStatusChip(bc.status)}</TableCell>
                <TableCell align="center">{formatDate(bc.deleted_at)}</TableCell>
                <TableCell align="center">
                  <Tooltip title="Restaurer">
                    <IconButton size="small" color="success" onClick={() => handleRestore(bc)}>
                      <RestoreIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );

  return (
    <Box>
      <Paper sx={{ p: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h5" sx={{ color: STOCK_COLOR, fontWeight: 600 }}>
            Bons de Commande
          </Typography>
          <PermissionButton
            moduleName="Stock"
            operation="add"
            miniModuleName="Bons Commande"
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpenAdd}
            sx={{ bgcolor: STOCK_COLOR, '&:hover': { bgcolor: '#1E88E5' } }}
          >
            Nouveau bon de commande
          </PermissionButton>
        </Box>

        <Tabs
          value={tabValue}
          onChange={(_, v) => {
            setTabValue(v);
            setPage(0);
          }}
          sx={{ mb: 2 }}
        >
          <Tab label={`Actifs (${totalCount})`} />
          <Tab label="Corbeille" />
        </Tabs>

        {tabValue === 0 && (
          <Stack direction="row" spacing={2} mb={2} alignItems="center">
            <TextField
              size="small"
              placeholder="Rechercher (référence, fournisseur, désignation)..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
                endAdornment: searchTerm && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearchTerm('')}>
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{ minWidth: 360 }}
            />
            {hasActive() && (
              <Button
                size="small"
                startIcon={<ClearIcon />}
                onClick={reset}
                sx={{ color: STOCK_COLOR }}
              >
                Effacer filtres colonnes
              </Button>
            )}
          </Stack>
        )}

        {tabValue === 0 ? renderActiveTable() : renderDeletedTable()}
      </Paper>

      {/* Status change menu */}
      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={closeStatusMenu}>
        <MenuItem onClick={() => triggerStatusChange(BonCommandeStatus.ENVOYEE, 'Envoyée')}>
          <SendIcon sx={{ mr: 1, color: 'info.main' }} fontSize="small" /> Marquer envoyée
        </MenuItem>
        <MenuItem onClick={() => triggerStatusChange(BonCommandeStatus.RECUE, 'Reçue')}>
          <ReceivedIcon sx={{ mr: 1, color: 'success.main' }} fontSize="small" /> Marquer reçue
        </MenuItem>
        <MenuItem onClick={() => triggerStatusChange(BonCommandeStatus.ANNULEE, 'Annulée')}>
          <CancelIcon sx={{ mr: 1, color: 'error.main' }} fontSize="small" /> Annuler
        </MenuItem>
        <MenuItem onClick={() => triggerStatusChange(BonCommandeStatus.BROUILLON, 'Brouillon')}>
          <ReopenIcon sx={{ mr: 1, color: 'grey.700' }} fontSize="small" /> Remettre brouillon
        </MenuItem>
      </Menu>

      <BonCommandeFormDialog
        open={formOpen}
        onClose={() => {
          setFormOpen(false);
          setFormMinimized(false);
        }}
        onSuccess={handleFormSuccess}
        bonCommandeId={editingId}
        minimized={formMinimized}
        onMinimizedChange={setFormMinimized}
      />

      <AlertDialog
        open={alertState.open}
        type={alertState.type}
        title={alertState.title}
        message={alertState.message}
        onClose={hideAlert}
        onConfirm={alertState.onConfirm}
        confirmText={alertState.confirmText}
        cancelText={alertState.cancelText}
        showCancel={alertState.showCancel}
      />

      {/* PDF Preview (print/download from the viewer toolbar) */}
      {pdfPreviewElement}
    </Box>
  );
};

export default BonsCommandeManagement;
